import { yupResolver } from '@hookform/resolvers/yup';
import * as Avatar from '-/Custom/Avatar';
import * as Header from '-/Custom/Header';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery } from 'urql';
import * as Yup from 'yup';
import ChatInfoInputs from '@/components/Chat/ChatInfoInputs';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import ModalKeyboardAwareScrollView from '@/components/Custom/ModalKeyboardAwareScrollView';
import ModalKeyboardStickyView from '@/components/Custom/ModalKeyboardStickyView';
import { graphql } from '@/gql/graphql';
import useGenerateImageUploadUrls from '@/hooks/useGenerateImageUploadUrls';
import useUploadImage from '@/hooks/useUploadImage';
import getOffsetColor from '@/utils/getOffsetColor';
import getRandomColor from '@/utils/getRandomColor';
import pickAvatar from '@/utils/pickAvatar';

type ChatDetailInfoUpdateFormValues = {
  title: string;
  color: string | null;
  avatarId: string | null;
  localAvatarUrl: string | null;
};

const GetChatDetailInfoUpdateScreen = graphql(
  /* GraphQL */ `
    query GetChatDetailInfoUpdateScreen($chatId: uuid!) {
      chat_by_pk(id: $chatId) {
        id
        title
        color
        avatar_id
      }
    }
  `,
);

const UpdateChat = graphql(/* GraphQL */ `
  mutation UpdateChat(
    $id: uuid!
    $title: String
    $color: String
    $avatarId: uuid
  ) {
    update_chat_by_pk(
      pk_columns: { id: $id }
      _set: { title: $title, color: $color, avatar_id: $avatarId }
    ) {
      id
      metadata
    }
  }
`);

const Schema: Yup.ObjectSchema<ChatDetailInfoUpdateFormValues> = Yup.object({
  title: Yup.string().default('').defined(),
  color: Yup.string().nullable().defined(),
  avatarId: Yup.string().nullable().defined(),
  localAvatarUrl: Yup.string().nullable().defined(),
}).defined();

export default function ChatDetailInfoUpdate() {
  const { chatId } = useLocalSearchParams<{ chatId?: string }>();
  const { back } = useRouter();
  const uploadImage = useUploadImage();
  const generateImageUploadUrls = useGenerateImageUploadUrls();
  const [defaultColor] = useState(() => getRandomColor());

  const [{ data }] = useQuery({
    query: GetChatDetailInfoUpdateScreen,
    variables: { chatId: chatId || '' },
    pause: !chatId,
    requestPolicy: 'cache-and-network',
  });

  const chat = data?.chat_by_pk;

  const form = useForm<ChatDetailInfoUpdateFormValues>({
    resolver: yupResolver(Schema),
    mode: 'onChange',
    defaultValues: {
      title: chat?.title || '',
      color: chat?.color || defaultColor,
      avatarId: chat?.avatar_id ?? null,
      localAvatarUrl: null,
    },
  });

  const title = useWatch({
    control: form.control,
    name: 'title',
    defaultValue: chat?.title || '',
  });
  const color = useWatch({
    control: form.control,
    name: 'color',
    defaultValue: chat?.color || defaultColor,
  });
  const localAvatarUrl = useWatch({
    control: form.control,
    name: 'localAvatarUrl',
    defaultValue: null,
  });
  const avatarId = useWatch({
    control: form.control,
    name: 'avatarId',
    defaultValue: chat?.avatar_id ?? null,
  });

  const [, updateChat] = useMutation(UpdateChat);

  const onPressCamera = async () => {
    const image = await pickAvatar();
    if (image?.uri) {
      form.setValue('localAvatarUrl', image.uri, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  return (
    <FormProvider {...form}>
      <Stack.Screen
        options={{
          title: 'Edit Chat',
          headerShown: true,
        }}
      />
      <ModalKeyboardAwareScrollView>
        <Header.Toolbar>
          <Header.Row>
            <Avatar.Pressable onPress={onPressCamera} borderRadius="full">
              <Avatar.default color={color || chat?.color} size="xl">
                <Avatar.Icon
                  name="camera-outline"
                  size="4xl"
                  color={getOffsetColor(color || chat?.color || '', false)}
                />
                {localAvatarUrl || avatarId || chat?.avatar_id ? (
                  <Avatar.Image
                    id={avatarId || chat?.avatar_id || undefined}
                    localUrl={localAvatarUrl}
                    size="xl"
                  />
                ) : null}
              </Avatar.default>
            </Avatar.Pressable>
            <Header.Title>{title || chat?.title || ''}</Header.Title>
          </Header.Row>
        </Header.Toolbar>
        {color ? <Header.ColorBar backgroundColor={color} /> : null}

        <ChatInfoInputs
          title={title}
          titlePlaceholder="Title"
          color={color}
          onChangeTitle={(next) =>
            form.setValue('title', next, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          onChangeColor={(next) =>
            form.setValue('color', next, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        />
      </ModalKeyboardAwareScrollView>

      <ModalKeyboardStickyView>
        <FormSaveButton
          info={{ text: 'Save' }}
          onPress={() =>
            form.handleSubmit(async (values) => {
              if (!chatId) {
                return;
              }

              let nextAvatarId: string | null = values.avatarId;

              if (values.localAvatarUrl) {
                const { data: uploadUrls } = await generateImageUploadUrls(1);
                const uploadUrl =
                  uploadUrls?.generateImageUploadUrls?.[0]?.uploadURL;

                nextAvatarId = await uploadImage(
                  `chat/${chatId}/avatar`,
                  values.localAvatarUrl,
                  uploadUrl || '',
                );
              }

              const { error } = await updateChat({
                id: chatId,
                title: values.title,
                color: values.color,
                avatarId: nextAvatarId,
              });

              if (!error) {
                back();
              }
            })()
          }
        />
      </ModalKeyboardStickyView>
    </FormProvider>
  );
}
